// stores/useLanguageStore.ts
import { createJSONStorage, persist } from 'zustand/middleware'

import { create } from 'zustand'

import { decryptData, encryptData } from '@/lib/crypto'
import { logger } from '@/lib/logger'

type LanguageState = {
  language: string
  setLanguage: (lang: string) => void
  initializeLanguage: (userLocale?: string | null) => string
}

export const useLanguageStore = create<LanguageState>()(
  persist(
    (set, get) => ({
      language: 'en',
      setLanguage: (lang) => set({ language: lang }),
      initializeLanguage: (userLocale) => {
        const supportedLangs = ['en', 'ar']
        let langToSet = 'en' // Default fallback

        // Priority 1: User's preferred locale (if authenticated)
        if (userLocale && supportedLangs.includes(userLocale)) {
          langToSet = userLocale
        }
        // Priority 2: Previously stored language
        else if (supportedLangs.includes(get().language)) {
          langToSet = get().language
        }
        // Priority 3: Browser language
        else {
          const browserLang = navigator.language.split('-')[0]
          if (supportedLangs.includes(browserLang)) {
            langToSet = browserLang
          }
        }

        set({ language: langToSet })
        return langToSet
      },
    }),
    {
      name: 'bbg-lang',
      version: 1, // Useful for future migrations
      storage: createJSONStorage(() => ({
        getItem: (name) => {
          const encrypted = localStorage.getItem(name)
          if (!encrypted) return null
          try {
            const decrypted = decryptData(encrypted)
            return decrypted
          } catch (error) {
            // Silently remove corrupted data
            localStorage.removeItem(name)
            return null
          }
        },
        setItem: (name, value) => {
          try {
            const encrypted = encryptData(value)
            localStorage.setItem(name, encrypted)
          } catch (error) {
            logger.error('Failed to encrypt language data', error)
            localStorage.setItem(name, value)
          }
        },
        removeItem: (name) => localStorage.removeItem(name),
      })),
    }
  )
)
